import React, { useEffect, useState } from "react";
import { fetchLostFound, createLostFound, updateLostFound, deleteLostFound, getRole } from "../services/api";
import { parseServerDate } from "../utils/time";
import { useT } from "../i18n/useT";

// Front Desk — Lost & Found register. Guests leave belongings behind; the desk
// logs the item, stores it, then returns it to its owner or disposes of it.
const S = {
  en: {
    title: "Lost & Found", subtitle: "Belongings guests left behind — logged, stored, then returned or disposed",
    all: "All", stored: "Stored", returned: "Returned", disposed: "Disposed",
    search: "Search description or guest…", add: "Add item", none: "No items logged yet.",
    f_description: "Description", f_location: "Where found", f_found_at: "Date found",
    f_found_by: "Found by", f_guest: "Guest name", f_storage: "Storage location", f_notes: "Notes",
    col_item: "Item", col_found: "Found", col_guest: "Guest", col_storage: "Storage", col_status: "Status",
    mark_returned: "Mark returned", mark_disposed: "Mark disposed", del: "Delete",
    returned_to_q: "Returned to (name)?", dispose_q: "Mark this item as disposed?",
    del_q: "Delete this item permanently?", err: "Could not save.", saving: "Saving…",
    required: "Description is required.",
  },
  am: {
    title: "የጠፉና የተገኙ", subtitle: "እንግዶች የተዉት ንብረት — ተመዝግቦ ተከማችቶ ይመለሳል ወይም ይወገዳል", // TODO verify am
    all: "ሁሉም", stored: "ተከማችቷል", returned: "ተመልሷል", disposed: "ተወግዷል", // TODO verify am
    search: "መግለጫ ወይም እንግዳ ፈልግ…", add: "ንብረት ጨምር", none: "እስካሁን የተመዘገበ ንብረት የለም።", // TODO verify am
    f_description: "መግለጫ", f_location: "የተገኘበት ቦታ", f_found_at: "የተገኘበት ቀን", // TODO verify am
    f_found_by: "ያገኘው", f_guest: "የእንግዳ ስም", f_storage: "የማከማቻ ቦታ", f_notes: "ማስታወሻ", // TODO verify am
    col_item: "ንብረት", col_found: "ተገኘ", col_guest: "እንግዳ", col_storage: "ማከማቻ", col_status: "ሁኔታ", // TODO verify am
    mark_returned: "እንደተመለሰ ምልክት አድርግ", mark_disposed: "እንደተወገደ ምልክት አድርግ", del: "ሰርዝ", // TODO verify am
    returned_to_q: "ለማን ተመለሰ (ስም)?", dispose_q: "ይህ ንብረት እንደተወገደ ይመዝገብ?", // TODO verify am
    del_q: "ይህን ንብረት እስከመጨረሻው ይሰረዝ?", err: "ማስቀመጥ አልተሳካም።", saving: "በማስቀመጥ ላይ…", // TODO verify am
    required: "መግለጫ ያስፈልጋል።", // TODO verify am
  },
  om: {
    title: "Bade & Argame", subtitle: "Qabeenya keessummoonni dhiisan — galmeeffamee kuufamee, deebi'a yookaan gatama", // TODO verify om
    all: "Hunda", stored: "Kuufame", returned: "Deebi'e", disposed: "Gatame", // TODO verify om
    search: "Ibsa yookaan keessummaa barbaadi…", add: "Meeshaa dabali", none: "Hanga ammaa meeshaan hin galmoofne.", // TODO verify om
    f_description: "Ibsa", f_location: "Bakka argame", f_found_at: "Guyyaa argame", // TODO verify om
    f_found_by: "Kan argate", f_guest: "Maqaa keessummaa", f_storage: "Bakka kuusaa", f_notes: "Yaadannoo", // TODO verify om
    col_item: "Meeshaa", col_found: "Argame", col_guest: "Keessummaa", col_storage: "Kuusaa", col_status: "Haala", // TODO verify om
    mark_returned: "Akka deebi'etti mallatteessi", mark_disposed: "Akka gatametti mallatteessi", del: "Haqi", // TODO verify om
    returned_to_q: "Eenyuuf deebi'e (maqaa)?", dispose_q: "Meeshaan kun akka gatametti galmaa'uu?", // TODO verify om
    del_q: "Meeshaa kana bara baraan haquu?", err: "Olkaa'uun hin milkoofne.", saving: "Olkaa'aa jira…", // TODO verify om
    required: "Ibsi barbaachisaadha.", // TODO verify om
  },
};

const STATUSES = ["stored", "returned", "disposed"];
const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: "7px 9px", border: "1px solid #d8dee2", borderRadius: 8, fontSize: 13 };
const ST_TINT = { stored: "#EEF2FB", returned: "#EAF3DE", disposed: "#F6EAEA" };
const ST_FG = { stored: "#0C447C", returned: "#27500A", disposed: "#A32D2D" };

const EMPTY = { description: "", location_found: "", found_at: "", found_by: "", guest_name: "", storage_location: "", notes: "" };

export default function LostFoundPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const canDelete = getRole() === "manager";
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setErr(false);
    setRows(null);
    fetchLostFound({ status: filter === "all" ? undefined : filter, q: q.trim() || undefined })
      .then(setRows)
      .catch(() => { setRows([]); setErr(true); });
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtDate = (v) => {
    const d = parseServerDate(v);
    return d ? d.toLocaleDateString() : "—";
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.description.trim()) { window.alert(t("required")); return; }
    setSaving(true);
    const body = {};
    Object.keys(form).forEach((k) => { if (form[k] !== "") body[k] = form[k]; });
    createLostFound(body)
      .then((created) => { setRows((rs) => [created, ...(rs || [])]); setForm(EMPTY); })
      .catch(() => window.alert(t("err")))
      .finally(() => setSaving(false));
  };

  const patch = (id, body) => updateLostFound(id, body)
    .then((u) => setRows((rs) => (rs || []).map((x) => (x.id === u.id ? u : x))))
    .catch(() => window.alert(t("err")));

  const markReturned = (x) => {
    const to = window.prompt(t("returned_to_q"), x.returned_to || x.guest_name || "");
    if (to == null) return;
    patch(x.id, { status: "returned", returned_to: to });
  };
  const markDisposed = (x) => {
    if (!window.confirm(t("dispose_q"))) return;
    patch(x.id, { status: "disposed" });
  };
  const remove = (x) => {
    if (!window.confirm(t("del_q"))) return;
    deleteLostFound(x.id)
      .then(() => setRows((rs) => (rs || []).filter((r) => r.id !== x.id)))
      .catch(() => window.alert(t("err")));
  };

  const runSearch = (e) => { e.preventDefault(); load(); };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {/* Add item */}
      <form onSubmit={submit} style={{ ...CARD, padding: 14, marginTop: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <input style={{ ...inp, flex: "2 1 240px" }} placeholder={t("f_description")} value={form.description} onChange={set("description")} />
          <input style={{ ...inp, flex: "1 1 160px" }} placeholder={t("f_location")} value={form.location_found} onChange={set("location_found")} />
          <input style={{ ...inp, flex: "1 1 140px" }} type="date" aria-label={t("f_found_at")} value={form.found_at} onChange={set("found_at")} />
          <input style={{ ...inp, flex: "1 1 140px" }} placeholder={t("f_found_by")} value={form.found_by} onChange={set("found_by")} />
          <input style={{ ...inp, flex: "1 1 160px" }} placeholder={t("f_guest")} value={form.guest_name} onChange={set("guest_name")} />
          <input style={{ ...inp, flex: "1 1 160px" }} placeholder={t("f_storage")} value={form.storage_location} onChange={set("storage_location")} />
          <input style={{ ...inp, flex: "2 1 240px" }} placeholder={t("f_notes")} value={form.notes} onChange={set("notes")} />
          <button className="btn primary small" type="submit" disabled={saving}>{saving ? t("saving") : t("add")}</button>
        </div>
      </form>

      {/* Filters + search */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16, alignItems: "center" }}>
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={filter === s ? "btn primary small" : "btn secondary small"}>{t(s)}</button>
        ))}
        <form onSubmit={runSearch} style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <input style={{ ...inp, width: 220 }} placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn secondary small" type="submit">🔍</button>
        </form>
      </div>

      {rows == null ? (
        <p className="muted" style={{ marginTop: 16 }}>…</p>
      ) : err ? (
        <div style={{ ...CARD, padding: 20, marginTop: 12, textAlign: "center" }} className="muted">{t("err")}</div>
      ) : rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 12, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((x) => (
            <div key={x.id} style={{ ...CARD, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 700 }}>{x.description}</div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {x.location_found ? `${x.location_found}` : "—"}{x.found_at ? ` · ${fmtDate(x.found_at)}` : ""}
                  {x.found_by ? ` · ${x.found_by}` : ""}
                </div>
                {x.notes && <div className="muted" style={{ fontSize: 12 }}>{x.notes}</div>}
                {x.status === "returned" && x.returned_to && (
                  <div className="muted" style={{ fontSize: 12 }}>{t("returned")}: {x.returned_to}{x.returned_at ? ` · ${fmtDate(x.returned_at)}` : ""}</div>
                )}
              </div>
              <div style={{ minWidth: 120 }}>
                <div className="muted" style={{ fontSize: 11 }}>{t("col_guest")}</div>
                <div style={{ fontSize: 13 }}>{x.guest_name || "—"}</div>
              </div>
              <div style={{ minWidth: 120 }}>
                <div className="muted" style={{ fontSize: 11 }}>{t("col_storage")}</div>
                <div style={{ fontSize: 13 }}>{x.storage_location || "—"}</div>
              </div>
              <span style={{ background: ST_TINT[x.status] || "#eef2f2", color: ST_FG[x.status] || "#5b6472", borderRadius: 16, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>{t(x.status)}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {x.status !== "returned" && (
                  <button className="btn secondary small" onClick={() => markReturned(x)}>{t("mark_returned")}</button>
                )}
                {x.status !== "disposed" && (
                  <button className="btn secondary small" onClick={() => markDisposed(x)}>{t("mark_disposed")}</button>
                )}
                {canDelete && (
                  <button onClick={() => remove(x)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>{t("del")}</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
